import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import Multiselect from "multiselect-react-dropdown";
import { useLocation, useNavigate } from "react-router-dom";
import { createBranch, updateBranch } from "../../../Services/branchService";
import {
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  FormHelperText,
  FormLabel,
  Switch,
} from "@mui/material";
import {
  Phone,
  Restore,
  Gite,
  Email,
  Public,
  Person,
  Business,
  Home,
  Map,
  MapsHomeWork,
  Category,
  Numbers,
  Opacity,
  LocationCity,
  RequestQuote,
  Flag,
  Assignment,
  OpenInBrowser,
  Tag,
  CreditScore,
  Check,
  Language,
  Inventory,
} from "@mui/icons-material/";
import { IBranch, IBranchForm, IList } from "../../../@types/branch";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useBranchDistrictContext } from "../../../Context/BranchDistrictContext";
import { useBranchContext } from "../../../Context/BranchContext";
import { IBranchDistrict } from "../../../@types/branchDistrict";
import { StyledButton } from "../../../Components/ui/CustomButton";
import Navbar from "../../../Components/Navbar";
import {
  validationObject,
  formVals,
  requiredValidation,
  optionalValidation,
} from "./BranchFormUtils";

export default function BranchForm(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const branchDistrictList = useBranchDistrictContext();
  const branchDropdowns = useBranchContext();
  const [branch, setBranch] = useState<IBranchForm | IBranch>(
    location.state.branch ?? formVals
  );
  const [error, setError] = useState(validationObject);
  const [submitError, setSubmitError] = useState(false);
  const [selectedOptionValue, setSelectedOptionValue] = useState<any>("");
  const [options, setOptions] = useState([]);
  const [editForm, setEditForm] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const lms = branchDropdowns?.lms || [];
  const branchStatus = branchDropdowns?.branchStatus || [];
  const credit = branchDropdowns?.credit || [];
  const density = branchDropdowns?.density || [];
  const districts = branchDistrictList?.branchDistrict || [];
  const productTypes = branchDropdowns?.productTypes || [];
  const operatingTypes = branchDropdowns?.operatingTypes || [];
  const legalProducts = branchDropdowns?.legalProducts || [];
  const underwritingTypes = branchDropdowns?.underwritingTypes ?? [];

  useEffect(() => {
    setBranch((prev: IBranchForm | IBranch) => ({
      ...prev,
      ...location.state.branch,
    }));
    setOptions(productTypes);
    let productNames = branch.branchProducts;
    setSelectedOptionValue(productNames!);
    branch.branchId === "" ? setEditForm(false) : setEditForm(true);
  }, []);

  const activationToggle = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      let value = !branch.isActive;
      setBranch((prev: IBranchForm | IBranch) => ({
        ...prev,
        isActive: value,
      }));
    } catch (error) {
      console.error(error);
    }
    e.preventDefault();
  };

  const findBranchToggle = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    try {
      let value = !branch.hasAccessToFindABranch;
      setBranch((prev: IBranchForm | IBranch) => ({
        ...prev,
        hasAccessToFindABranch: value,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const isCentralSalesToggle = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    try {
      let value = !branch.isCentralSales;
      setBranch((prev: IBranchForm | IBranch) => ({
        ...prev,
        isCentralSales: value,
      }));
    } catch (error) {
      console.error(error);
    }
  };

  //multi select drop down methods
  const onSearch = () => {
    callSearch();
  };
  const callSearch = async () => {
    const response = productTypes;
    setOptions(response);
  };

  const onSelect = (selectedList: any) => {
    branch.branchProducts = [];
    setSelectedOptionValue(selectedList);
    branch.branchProducts = selectedList;
  };

  const onRemove = (selectedList: any) => {
    branch.branchProducts = [];
    setSelectedOptionValue(selectedList);
    branch.branchProducts = selectedList;
  };

  //form value change methods
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const name = e.target.name as keyof IBranch;

    setBranch((prev: IBranchForm | IBranch) => ({ ...prev, [name]: value }));
  }, []);

  const branchStatusChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBranch((prev: IBranchForm | IBranch) => ({
      ...prev,
      branchStatusId: value,
    }));
    if (Number(value) !== 0) {
      setError((errors) => ({
        ...errors,
      }));
    }
  }, []);

  const branchLegalProductChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setBranch((prev: IBranchForm | IBranch) => ({
        ...prev,
        legalProductTypeId: value,
      }));

      let legalProd = Number(value);

      const validateAndSetError = (field: string, value: any) => {
        const isNotValid = requiredValidation(
          field,
          value,
          branch.branchStatusId,
          legalProd
        );

        if (isNotValid) {
          setError((errors) => ({
            ...errors,
            [field]: isNotValid,
          }));
        }
      };

      if (legalProd === 3 || legalProd === 1) {
        validateAndSetError(
          "legalEntitySmallLoan",
          branch.legalEntitySmallLoan
        );

        setError((errors) => ({
          ...errors,
          legalEntityLargeLoan: false,
        }));

        legalProd === 1 &&
          setBranch((prev: IBranchForm | IBranch) => ({
            ...prev,
            legalEntityLargeLoan: "",
          }));
      }
      if (legalProd === 2 || legalProd === 3) {
        validateAndSetError(
          "legalEntityLargeLoan",
          branch.legalEntityLargeLoan
        );

        setError((errors) => ({
          ...errors,
          legalEntitySmallLoan: false,
        }));

        legalProd === 2 &&
          setBranch((prev: IBranchForm | IBranch) => ({
            ...prev,
            legalEntitySmallLoan: "",
          }));
      }
    },
    []
  );

  const validateInput = useCallback(
    (e: any) => {
      const { value, name } = e.target;
      let isNotValid = requiredValidation(
        name,
        value,
        branch.branchStatusId,
        branch.legalProductTypeId
      );
      setError((errors) => ({ ...errors, [name]: isNotValid }));
    },
    [branch.branchStatusId, branch.legalProductTypeId]
  );

  const optionalInputValdiation = useCallback((e: any) => {
    const { value, name } = e.target;
    let isNotValid = optionalValidation(name, value);
    setError((errors) => ({ ...errors, [name]: isNotValid }));
  }, []);

  const validationCheckOnSubmission = (form: IBranchForm) => {
    let isNotValid = false;
    Object.keys(form).forEach((key: string) => {
      isNotValid = requiredValidation(
        key,
        form[key as keyof IBranchForm],
        form.branchStatusId,
        form.legalProductTypeId
      );

      if (
        form[key as keyof IBranchForm] !== "" &&
        form[key as keyof IBranchForm] !== 0
      ) {
        isNotValid = optionalValidation(key, form[key as keyof IBranchForm]);
      }
      setError((errors) => ({ ...errors, [key]: isNotValid }));
    });
    const formNotValid = Object.values(error).some((value) => value === true);
    if (!formNotValid) return false;
    else {
      setSubmitError(true);
      return true;
    }
  };

  const validateFields = (
    fields: string[],
    branch: IBranchForm,
    errors: any
  ) => {
    fields.forEach((field) => {
      const isInvalid = optionalValidation(
        field,
        branch[field as keyof IBranchForm]
      );
      if (isInvalid) {
        errors[field as keyof typeof errors] = isInvalid;
      }
    });
    return errors;
  };

  const handleSave = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const fieldsToValidate = ["legalAddress"];
      let errors = { ...error };

      errors = validateFields(fieldsToValidate, branch, errors);
      const formNotValid =
        validationCheckOnSubmission(branch) || errors.legalAddress;

      if (!formNotValid) {
        setSubmitting(true);
        await updateBranch(branch as IBranch);
        branchDropdowns?.load();
        navigate(`/branch-table/${branch.branchId}`);
        setSubmitting(false);
      } else {
        setError(errors);
      }
    },
    [
      branch,
      error,
      validationCheckOnSubmission,
      setSubmitting,
      updateBranch,
      branchDropdowns,
      navigate,
      setError,
    ]
  );

  const handleAdd = useCallback(
    async (e: React.FormEvent) => {
      try {
        e.preventDefault();
        setSubmitting(true);
        const formNotValid = validationCheckOnSubmission(branch);
        let errors = error;
        if (!formNotValid) {
          await createBranch(branch as IBranch);
          branchDropdowns?.load();
          navigate("/branch-table");
        } else {
          setSubmitError(true);
          setError(errors);
        }
      } catch (error) {
        console.error("Error creating branch:", error);
      } finally {
        setSubmitting(false);
      }
    },
    [
      branch,
      validationCheckOnSubmission,
      setSubmitting,
      createBranch,
      branchDropdowns,
      navigate,
    ]
  );

  const cancel = () => {
    editForm
      ? navigate(`/branch-table/${branch.branchId}`)
      : navigate("/branch-table");
  };

  return (
    <>
      <Navbar />
      <div className="paper-content">
        <div className="no-padding">
          <h1 className="grid-page-title"> Branch ID - {branch.branchId}</h1>
        </div>
        <Paper elevation={10} sx={{ padding: 2, mt: 1, width: "100%" }}>
          <FormControl>
            <Grid2 container spacing={2}>
              <Grid2 xs={10}>
                <em style={{ color: "gray" }}>Branch Information</em>
              </Grid2>
              <Grid2 xs={2}>
                <Switch
                  color="error"
                  checked={branch.isActive}
                  onChange={activationToggle}
                  inputProps={{ "aria-label": "controlled" }}
                />
                <FormLabel>
                  Branch Is {branch.isActive ? "Active" : "Inactive"}
                </FormLabel>
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Business />
                  Branch ID
                </div>
                <TextField
                  disabled={editForm}
                  value={branch.branchId}
                  error={error.branchId}
                  autoFocus
                  fullWidth
                  onBlur={validateInput}
                  onChange={handleChange}
                  helperText={
                    error.branchId && "Please enter a 4 digit long number"
                  }
                  type="number"
                  required
                  id="outlined-basic"
                  name="branchId"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Numbers />
                  Code
                </div>
                <TextField
                  required
                  disabled={editForm}
                  value={branch.brCode}
                  error={error.brCode}
                  onBlur={validateInput}
                  onChange={handleChange}
                  helperText={
                    error.brCode &&
                    "Please enter a value starting with state code (i.e. NY0000)"
                  }
                  fullWidth
                  name="brCode"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <LocationCity />
                  District
                </div>
                {districts && districts.length > 0 && (
                  <TextField
                    select
                    fullWidth
                    error={error.branchDistrictId}
                    onBlur={validateInput}
                    onChange={handleChange}
                    value={branch.branchDistrictId ?? ""}
                    helperText={
                      error.branchDistrictId && "Please choose a value"
                    }
                    id="outlined-districts"
                    name="branchDistrictId"
                    variant="outlined"
                  >
                    {districts.map((district: IBranchDistrict) => (
                      <MenuItem
                        key={district.branchDistrictId}
                        value={district.branchDistrictId}
                      >
                        {district.branchDistrictId} - {district.title}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Flag />
                  Brand
                </div>
                <TextField
                  fullWidth
                  select
                  onChange={handleChange}
                  value={branch.brand}
                  error={error.brand}
                  onBlur={validateInput}
                  helperText={error.brand && "Please choose a value"}
                  id="outlined-basic"
                  name="brand"
                  variant="outlined"
                >
                  <MenuItem key={1} value={"Covington Credit"}>
                    Covington Credit
                  </MenuItem>
                  <MenuItem key={2} value={"Heights Finance"}>
                    Heights Finance
                  </MenuItem>
                  <MenuItem key={3} value={"Quick Credit"}>
                    Quick Credit
                  </MenuItem>
                  <MenuItem key={4} value={"Southern Finance"}>
                    Southern Finance
                  </MenuItem>
                  <MenuItem key={5} value={"First Heritage"}>
                    First Heritage
                  </MenuItem>
                </TextField>
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Assignment />
                  Brand Slug
                </div>
                <TextField
                  select
                  fullWidth
                  onChange={handleChange}
                  error={error.brandSlug}
                  onBlur={validateInput}
                  value={branch.brandSlug}
                  helperText={error.brandSlug && "Please choose a value"}
                  id="outlined-basic"
                  name="brandSlug"
                  variant="outlined"
                >
                  <MenuItem key={1} value={"covington-credit"}>
                    covington-credit
                  </MenuItem>
                  <MenuItem key={2} value={"heights-finance"}>
                    heights-finance
                  </MenuItem>
                  <MenuItem key={3} value={"quick-credit"}>
                    quick-credit
                  </MenuItem>
                  <MenuItem key={4} value={"southern-finance"}>
                    southern-finance
                  </MenuItem>
                  <MenuItem key={5} value={"first-heritage"}>
                    first-heritage
                  </MenuItem>
                </TextField>
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <RequestQuote />
                  Max Loan Amount
                </div>
                <TextField
                  onChange={handleChange}
                  value={branch.maxLoanAmount}
                  error={error.maxLoanAmount}
                  onBlur={validateInput}
                  fullWidth
                  helperText={
                    error.maxLoanAmount && "Please enter a value under 99,999"
                  }
                  type="number"
                  id="outlined-basic"
                  name="maxLoanAmount"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Check />
                  Status
                </div>
                {branchStatus && branchStatus.length > 0 && (
                  <TextField
                    select
                    required
                    fullWidth
                    onChange={branchStatusChange}
                    value={branch.branchStatusId}
                    error={error.branchStatusId}
                    onBlur={validateInput}
                    helperText={error.branchStatusId && "Please choose a value"}
                    id="outlined-basic"
                    name="branchStatusId"
                    variant="outlined"
                  >
                    {branchStatus.map((branchStatus: IList) => (
                      <MenuItem key={branchStatus.id} value={branchStatus.id}>
                        {branchStatus.id} - {branchStatus.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <CreditScore />
                  Credit Bureau
                </div>
                {credit && credit.length > 0 && (
                  <TextField
                    select
                    required
                    fullWidth
                    onChange={handleChange}
                    value={branch.creditBureauTypeId ?? ""}
                    error={error.creditBureauTypeId}
                    onBlur={validateInput}
                    helperText={
                      error.creditBureauTypeId && "Please choose a value"
                    }
                    id="outlined-basic"
                    name="creditBureauTypeId"
                    variant="outlined"
                  >
                    {credit.map((credit: IList) => (
                      <MenuItem key={credit.id} value={credit.id}>
                        {credit.id} - {credit.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Tag />
                  Loan Management System
                </div>{" "}
                {lms && lms.length > 0 && (
                  <TextField
                    select
                    required
                    fullWidth
                    onChange={handleChange}
                    value={branch.lmsId ?? ""}
                    error={error.lmsId}
                    onBlur={validateInput}
                    helperText={error.lmsId && "Please choose a value"}
                    id="outlined-basic"
                    name="lmsId"
                    variant="outlined"
                  >
                    {lms.map((lms: IList) => (
                      <MenuItem key={lms.id} value={lms.id}>
                        {lms.id} - {lms.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Opacity />
                  Density Type
                </div>
                {density && density.length > 0 && (
                  <TextField
                    select
                    fullWidth
                    onChange={handleChange}
                    value={branch.branchDensityTypeId}
                    error={error.branchDensityTypeId}
                    onBlur={validateInput}
                    helperText={
                      error.branchDensityTypeId && "Please choose a value"
                    }
                    id="outlined-basic"
                    name="branchDensityTypeId"
                    variant="outlined"
                  >
                    {density.map((density: IList) => (
                      <MenuItem key={density.id} value={density.id}>
                        {density.id} - {density.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Public />
                  Time Zone
                </div>
                <TextField
                  required
                  fullWidth
                  onChange={handleChange}
                  value={branch.timeZone}
                  error={error.timeZone}
                  onBlur={validateInput}
                  helperText={error.timeZone && "Please enter a value"}
                  id="outlined-time"
                  name="timeZone"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Inventory />
                  Product Type
                </div>

                {options ? (
                  <Multiselect
                    onSearch={onSearch}
                    options={options}
                    selectedValues={selectedOptionValue}
                    onSelect={onSelect}
                    onRemove={onRemove}
                    style={{
                      inputField: {
                        margin: "5px",
                      },
                      searchBox: {
                        border: "none",
                        minHeight: "50px",
                        borderBottom: "1px solid black",
                        borderRadius: "0px",
                      },
                    }}
                    displayValue="title"
                  />
                ) : null}
              </Grid2>
              <Grid2 xs={4}>
                <div className="grid-title">
                  <Gite />
                  Street Address
                </div>
                <TextField
                  margin="normal"
                  fullWidth
                  onChange={handleChange}
                  value={branch.address1}
                  error={error.address1}
                  onBlur={validateInput}
                  helperText={error.address1 && "Please enter a value"}
                  id="outlined-basic"
                  name="address1"
                  label="Street Address"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={8}>
                <div className="grid-title">
                  <Gite />
                  City, State, Zipcode
                </div>
                <TextField
                  margin="normal"
                  onChange={handleChange}
                  value={branch.city}
                  error={error.city}
                  onBlur={validateInput}
                  helperText={error.city && "Please enter a value"}
                  id="outlined-basic"
                  name="city"
                  label="City"
                  variant="outlined"
                />
                <TextField
                  margin="normal"
                  inputProps={{ maxLength: 2 }}
                  onChange={handleChange}
                  value={branch.state}
                  error={error.state}
                  onBlur={validateInput}
                  helperText={error.state && "Please enter a value"}
                  id="outlined-basic"
                  name="state"
                  label="State"
                  variant="outlined"
                />
                <TextField
                  margin="normal"
                  onChange={handleChange}
                  value={branch.zip}
                  error={error.zip}
                  onBlur={validateInput}
                  helperText={
                    error.zip && "Please enter a valid 5 digit zipcode"
                  }
                  id="outlined-basic"
                  name="zip"
                  label="Zipcode"
                  variant="outlined"
                />
              </Grid2>

              <Grid2 xs={4}>
                <div className="grid-title">
                  <Map />
                  Legal Address
                </div>
                <TextField
                  margin="normal"
                  fullWidth
                  onChange={handleChange}
                  error={error.legalAddress}
                  value={branch.legalAddress}
                  onBlur={optionalInputValdiation}
                  helperText={
                    error.legalAddress &&
                    "Please input a alphanumeric value that is less than 255 characters long"
                  }
                  inputProps={{ maxLength: 255 }}
                  id="outlined-basic"
                  name="legalAddress"
                  label="Legal Address"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Home />
                  County
                </div>
                <TextField
                  margin="normal"
                  fullWidth
                  onChange={handleChange}
                  value={branch.county}
                  error={error.county}
                  onBlur={optionalInputValdiation}
                  helperText={
                    error.county &&
                    "Please input a alphanumeric value that is less than 100 characters long"
                  }
                  inputProps={{ maxLength: 100 }}
                  id="outlined-basic"
                  name="county"
                  label="County"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Category />
                  Legal Product
                </div>
                {legalProducts && legalProducts.length > 0 && (
                  <TextField
                    margin="normal"
                    select
                    fullWidth
                    disabled={editForm}
                    onChange={branchLegalProductChange}
                    error={error.legalProductTypeId}
                    helperText={
                      error.legalProductTypeId && "Please choose a value"
                    }
                    value={branch.legalProductTypeId}
                    onBlur={validateInput}
                    id="outlined-basic"
                    name="legalProductTypeId"
                    label="Legal Product"
                    variant="outlined"
                  >
                    {legalProducts.length > 0 &&
                      legalProducts.map((legalProducts: IList) => (
                        <MenuItem
                          key={legalProducts.id}
                          value={legalProducts.id}
                        >
                          {legalProducts.id} - {legalProducts.name}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              </Grid2>
              <Grid2 xs={4}>
                <div className="grid-title">
                  <MapsHomeWork />
                  Legal Entity Small Loan
                </div>
                <TextField
                  margin="normal"
                  fullWidth
                  disabled={branch?.legalProductTypeId === 2}
                  onChange={handleChange}
                  error={error.legalEntitySmallLoan}
                  value={branch.legalEntitySmallLoan}
                  onBlur={validateInput}
                  inputProps={{ maxLength: 255 }}
                  helperText={
                    error.legalEntitySmallLoan &&
                    "Please input a alphanumeric value that is less than 255 characters long"
                  }
                  id="outlined-basic"
                  label="Legal Entity"
                  name="legalEntitySmallLoan"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={4}>
                <div className="grid-title">
                  <MapsHomeWork />
                  Legal Entity Large Loan
                </div>
                <TextField
                  margin="normal"
                  fullWidth
                  disabled={branch?.legalProductTypeId === 1}
                  onChange={handleChange}
                  error={error.legalEntityLargeLoan}
                  value={branch.legalEntityLargeLoan}
                  onBlur={validateInput}
                  inputProps={{ maxLength: 255 }}
                  helperText={
                    error.legalEntityLargeLoan &&
                    "Please input a alphanumeric value that is less than 255 characters long"
                  }
                  id="outlined-basic"
                  label="Legal Entity"
                  name="legalEntityLargeLoan"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Category />
                  Operating Type
                </div>
                {operatingTypes && operatingTypes.length > 0 && (
                  <TextField
                    margin="normal"
                    select
                    fullWidth
                    onChange={handleChange}
                    error={error.branchOperatingType}
                    helperText={
                      error.branchOperatingType && "Please choose a value"
                    }
                    value={branch.branchOperatingTypeId ?? ""}
                    id="outlined-basic"
                    name="branchOperatingTypeId"
                    label="Operating Type"
                    variant="outlined"
                  >
                    {operatingTypes.length > 0 &&
                      operatingTypes.map((operatingType: IList) => (
                        <MenuItem
                          key={operatingType.id}
                          value={operatingType.id}
                        >
                          {operatingType.id} - {operatingType.name}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <CreditScore />
                  Underwriting Type
                </div>
                {underwritingTypes && underwritingTypes.length > 0 && (
                  <TextField
                    select
                    required
                    fullWidth
                    onChange={handleChange}
                    value={branch.underwritingTypeId ?? ""}
                    error={error.underwritingTypeId}
                    onBlur={validateInput}
                    helperText={
                      error.underwritingTypeId && "Please choose a value"
                    }
                    id="outlined-basic"
                    name="underwritingTypeId"
                    variant="outlined"
                  >
                    {underwritingTypes.map((under: IList) => (
                      <MenuItem key={under.id} value={under.id}>
                        {under.id} - {under.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Grid2>
             
              <Grid2 xs={12}>
                <Switch
                  required
                  color="error"
                  checked={branch.hasAccessToFindABranch}
                  onChange={findBranchToggle}
                  inputProps={{ "aria-label": "controlled" }}
                />
                <FormLabel>Has Access to Find a Branch</FormLabel>
              </Grid2>
              <Grid2 xs={4}>
                <Switch
                  required
                  color="error"
                  checked={branch.isCentralSales || false}
                  onChange={isCentralSalesToggle}
                  inputProps={{ "aria-label": "controlled" }}
                />
                <FormLabel>Central Sales Branch</FormLabel>
              </Grid2>
              
              <Grid2 xs={12}>
                {branch.branchStatusId === 0 ? (
                  <FormHelperText>
                    {" "}
                    The following fields are required to set Branch{" "}
                    {branch.branchId} to the status of Open
                  </FormHelperText>
                ) : (
                  ""
                )}
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Phone />
                  Phone
                </div>
                <TextField
                  required
                  fullWidth
                  onChange={handleChange}
                  value={branch.phone}
                  error={error.phone}
                  onBlur={validateInput}
                  helperText={
                    error.phone && "Please enter a value 10 digit phone number"
                  }
                  id="outlined-basic"
                  name="phone"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Email />
                  Email
                </div>
                <TextField
                  fullWidth
                  onChange={handleChange}
                  error={error.branchEmail}
                  value={branch.branchEmail}
                  onBlur={validateInput}
                  helperText={error.branchEmail && "Please enter a value"}
                  id="outlined-basic"
                  name="branchEmail"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end"></InputAdornment>
                    ),
                  }}
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Restore />
                  Hours
                </div>
                <TextField
                  fullWidth
                  onChange={handleChange}
                  value={branch.hours}
                  error={error.hours}
                  onBlur={validateInput}
                  helperText={error.hours && "Please enter a value"}
                  id="outlined-basic"
                  name="hours"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={6}>
                <div className="grid-title">
                  <OpenInBrowser />
                  Webpage
                </div>
                <TextField
                  margin="normal"
                  fullWidth
                  onChange={handleChange}
                  value={branch.webPage}
                  error={error.webPage}
                  onBlur={validateInput}
                  helperText={error.webPage && "Please enter a valid website"}
                  id="outlined-basic"
                  name="webPage"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={6}>
                <div className="grid-title">
                  <Language />
                  Longitude - Latittude
                </div>
                <TextField
                  margin="normal"
                  onChange={handleChange}
                  value={branch.longitude}
                  error={error.longitude}
                  onBlur={validateInput}
                  type="number"
                  helperText={
                    error.longitude && "Please enter a valid longitude"
                  }
                  id="outlined-basic"
                  name="longitude"
                  label="Longitude"
                  variant="outlined"
                />
                <TextField
                  margin="normal"
                  onChange={handleChange}
                  value={branch.latitude}
                  error={error.latitude}
                  onBlur={validateInput}
                  type="number"
                  helperText={error.latitude && "Please enter a valid latitude"}
                  id="outlined-basic"
                  name="latitude"
                  label="Latitude"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Person />
                  Manager Name
                </div>
                <TextField
                  fullWidth
                  onChange={handleChange}
                  value={branch.manager}
                  error={error.manager}
                  onBlur={validateInput}
                  helperText={error.manager && "Please enter a name"}
                  id="outlined-basic"
                  name="manager"
                  variant="outlined"
                />
              </Grid2>
              <Grid2 xs={3}>
                <div className="grid-title">
                  <Email />
                  Manager Email
                </div>
                <TextField
                  fullWidth
                  onChange={handleChange}
                  value={branch.mgrEmail}
                  error={error.mgrEmail}
                  onBlur={validateInput}
                  helperText={error.mgrEmail && "Please enter a valid email"}
                  id="outlined-basic"
                  name="mgrEmail"
                  variant="outlined"
                />
              </Grid2>
               <Grid2 xs={3}>
                                <div className="grid-title">
                   <Gite />
                  Branch Operating State
                </div>
                              <TextField
                  inputProps={{ maxLength: 2 }}
                  onChange={handleChange}
                  value={branch.branchOperatingState}
                  error={error.branchOperatingState}
                  onBlur={validateInput}
                  helperText={error.branchOperatingState && "Please enter a value"}
                  id="outlined-basic"
                  name="branchOperatingState"
                   variant="outlined"
                />
              </Grid2>
              <Grid2 xs={12}>
                <div className="button-toolbar">
                  {submitError ? (
                    <FormHelperText error={submitError}>
                      {" "}
                      Please enter the required fields before proceeding{" "}
                    </FormHelperText>
                  ) : (
                    ""
                  )}
                  <StyledButton
                    className="button"
                    disabled={submitting}
                    type="submit"
                    style={{
                      padding: 1,
                      margin: 2,
                    }}
                    onClick={editForm ? handleSave : handleAdd}
                  >
                    {editForm ? " Save " : " Add "}
                  </StyledButton>
                  <StyledButton
                    className="button"
                    type="button"
                    disabled={submitting}
                    style={{
                      margin: 2,
                    }}
                    onClick={cancel}
                  >
                    Cancel
                  </StyledButton>
                </div>
              </Grid2>
            </Grid2>
          </FormControl>
        </Paper>
      </div>
    </>
  );
}
